import React from 'react';

// Matches OTP_LENGTH in auth/otp_store.py.
export const CODE_LENGTH = 6;

export function CheckIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M4.5 10.5l3.5 3.5 7.5-7.5" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

// Signup and settings sit on different palettes — the signup pages are warm, the
// dashboard uses the accent — so the two tones live here rather than forcing one screen to
// look like a transplant from the other.
const TONES = {
    signup: {
        shell: 'rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 py-3.5',
        label: 'text-[11px] font-black uppercase tracking-[0.18em] text-[#606060]',
        value: 'mt-0.5 truncate text-[12px] font-bold text-[#414141]',
        verified: 'bg-[#e8f6ee] text-[#1c5c3c]',
        resend: 'text-[#D81F26] hover:text-[#414141]',
        input: 'mt-2.5 h-11 w-full rounded-lg border border-[#d8cbbd] bg-white px-4 text-center font-mono text-[18px] font-bold tracking-[0.35em] text-[#414141] outline-none transition placeholder:tracking-[0.2em] placeholder:text-[#d8cbbd] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10 disabled:opacity-60',
        muted: 'text-[#606060]',
    },
    dashboard: {
        shell: 'rounded-xl border border-[#d9d9d9] bg-[#F9F9F9] px-4 py-3.5',
        label: 'text-[11px] font-black uppercase tracking-[0.18em] text-[#414141]',
        value: 'mt-0.5 truncate text-[12px] font-bold text-[#414141]',
        verified: 'bg-[#e8f6ee] text-[#1c5c3c]',
        resend: 'text-[#D81F26] hover:text-[#B8181E]',
        input: 'mt-2.5 h-11 w-full rounded-lg border border-[#d9d9d9] bg-white px-4 text-center font-mono text-[18px] font-bold tracking-[0.35em] text-[#414141] outline-none transition placeholder:tracking-[0.2em] placeholder:text-[#d5d5d5] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10 disabled:opacity-60',
        muted: 'text-[#414141]',
    },
};

/* One verification channel: the masked destination, a 6-digit box, a resend control and
   the confirmed state. Verifying happens as soon as the sixth digit lands, so the owner
   finds out immediately instead of at submit.

   Shared by signup (proving a brand-new account's details) and by settings (proving a
   detail that is being changed). The two flows hit different endpoints but the box, the
   tick and the countdown behave identically, so there is one of it. */
export default function VerificationField({
    id, label, sentTo, value, onChange, verified, checking, cooldown, onResend,
    disabled, status, tone = 'signup',
}) {
    const t = TONES[tone] || TONES.signup;
    const resendLabel = cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code';
    return (
        <div className={t.shell}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <span className={t.label}>{label}</span>
                    <p className={t.value}>{sentTo || '—'}</p>
                </div>
                {verified ? (
                    <span className={`flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.1em] ${t.verified}`}>
                        <CheckIcon className="h-3.5 w-3.5" />
                        Verified
                    </span>
                ) : (
                    <button
                        type="button"
                        onClick={onResend}
                        disabled={disabled || cooldown > 0}
                        aria-label={resendLabel}
                        className={`shrink-0 text-[11px] font-black uppercase tracking-[0.1em] transition disabled:cursor-not-allowed disabled:text-[#a9a9a9] ${t.resend}`}
                    >
                        {resendLabel}
                    </button>
                )}
            </div>

            {verified ? null : (
                <>
                    <input
                        id={id}
                        type="text"
                        // The visible label above is a span, not a <label for>, so the
                        // box needs its own name for screen readers.
                        aria-label={`${label} code`}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder={'—'.repeat(CODE_LENGTH)}
                        maxLength={CODE_LENGTH}
                        value={value}
                        disabled={disabled}
                        onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
                        className={t.input}
                    />
                    {checking ? (
                        <p className={`mt-1.5 text-[11px] font-bold ${t.muted}`}>Checking…</p>
                    ) : status ? (
                        <p className="mt-1.5 text-[11px] font-bold text-red-600">{status}</p>
                    ) : null}
                </>
            )}
        </div>
    );
}
